<script setup lang="ts">
import { ref } from "vue";
import type { CmsElementPluebelCmsElementsFaq } from "~/composables/useCustom";

const props = defineProps<{
  content: CmsElementPluebelCmsElementsFaq;
}>();

const { headline, faq_elements } = props.content.config;
const activeAccordion = ref<string | null>(null);

const toggleAccordion = (id: string) => {
  activeAccordion.value = activeAccordion.value === id ? null : id;
};

const onEnter = (el: Element) => {
  const height = el.scrollHeight;
  const animation = el.animate(
    [
      { height: '0', opacity: 0 },
      { height: `${height}px`, opacity: 1 }
    ],
    {
      duration: 300,
      easing: 'ease-out'
    }
  );
  animation.onfinish = () => {
    (el as HTMLElement).style.height = 'auto';
  };
};

const onLeave = (el: Element) => {
  const height = el.scrollHeight;
  (el as HTMLElement).style.height = `${height}px`;
  
  void el.offsetHeight;
  
  const animation = el.animate(
    [
      { height: `${height}px`, opacity: 1 },
      { height: '0', opacity: 0 }
    ],
    {
      duration: 300,
      easing: 'ease-in'
    }
  );

  (el as HTMLElement).style.height = '0';
  
  animation.onfinish = () => {
    (el as HTMLElement).style.height = '';
  };
};
</script>

<template>
  <section class="faq-section">
    <div class="container">
      <div class="max-w-[1110px] w-full m-auto relative">
        <h3>{{ headline.value }}</h3>
        <div class="accordian-wrapper w-full">
          <div
            v-for="faq in faq_elements.value"
            :key="faq.id"
            class="accordian-item py-4"
          >
            <div
              class="accordian-head pr-6"
              :class="{ active: activeAccordion === faq.id }"
              @click="toggleAccordion(faq.id)"
            >
              <h5
                class="normal-case font-bold text-lg md:text-xl after:hidden leading-6 flex items-start justify-between cursor-pointer font-redit_sans_con"
              >
                {{ faq.faq_headline }}
                <span 
                    class="accordion-plus absolute right-0 pt-1"
                    :class="{ hidden: activeAccordion === faq.id }"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                      <path d="M10.5 3V17" stroke="#5C6566" stroke-width="2" stroke-linecap="round"/>
                      <path d="M3.5 10H17.5" stroke="#5C6566" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </span>
                  <span 
                    class="accordion-minus absolute right-0 pt-1"
                    :class="{ hidden: activeAccordion !== faq.id }"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                      <path d="M3.5 10H17.5" stroke="#5C6566" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </span>
              </h5>
            </div>
            <transition
              name="slide"
              @enter="onEnter"
              @leave="onLeave"
            >
              <div
                v-show="activeAccordion === faq.id"
                class="accordian-details leading-6 pt-3 text-base md:text-lg"
              >
                <p>
                  {{ faq.faq_text }}
                </p>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  overflow: hidden;
}

.slide-enter-from,
.slide-leave-to {
  height: 0;
  opacity: 0;
}
</style>
